import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/auth'

export const api: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 15_000,
})

// Attach access token on outgoing requests.
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

// Track an in-flight refresh so concurrent 401s share the same retry.
let refreshing: Promise<string | null> | null = null

async function tryRefresh(): Promise<string | null> {
  if (refreshing) return refreshing
  refreshing = (async () => {
    try {
      const res = await axios.post<{ accessToken: string }>(
        '/api/auth/refresh',
        {},
        { withCredentials: true },
      )
      const newToken = res.data.accessToken
      useAuthStore.getState().setAccessToken(newToken)
      return newToken
    } catch {
      useAuthStore.getState().clear()
      return null
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined

    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      // Don't try to refresh on auth endpoints themselves.
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/refresh')
    ) {
      original._retry = true
      const newToken = await tryRefresh()
      if (newToken) {
        original.headers?.set('Authorization', `Bearer ${newToken}`)
        return api.request(original)
      }
    }
    return Promise.reject(error)
  },
)
